import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Tabs, Tab, Button, Typography, Stack, Grid, TextField,
  MenuItem, Alert, IconButton, Chip, Divider, CircularProgress,
  Tooltip, useMediaQuery, useTheme,
} from '@mui/material';
import {
  ArrowBack, Edit, Save, Cancel, Add, Delete, Star, StarBorder,
  Email, Phone, Person, Business, AttachMoney, ContactMail,
  CloudSync as ZohoIcon, Store as AffiliateIcon, InfoOutlined,
} from '@mui/icons-material';
import { useConfirmDialog } from './ConfirmDialog';
import {
  updateCompany,
  getCompanyContacts, createCompanyContact, updateContact, deleteContact,
  getAffiliates, createAffiliate, updateAffiliate, deleteAffiliate,
  getEmployees, getRecurringEntrySchedules, createRecurringEntrySchedule,
  updateRecurringEntrySchedule, deleteRecurringEntrySchedule, runRecurringEntrySchedule,
} from '../api';
import { useAuth } from '../context/AuthContext';

const TERMS_OPTIONS = ['Due on Receipt', 'Net 7', 'Net 15', 'Net 30', 'Net 45', 'Net 60', 'Net 90'];
const EXPORT_FORMATS = [
  { value: 'standard', label: 'PDF Invoice' },
  { value: 'excel', label: 'Excel Invoice' },
  { value: 'ferc', label: 'FERC Format (Excel)' },
];

const ADDRESS_FIELDS = [
  { key: 'address', label: 'Street' },
  { key: 'street2', label: 'Street 2' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'zip', label: 'ZIP / Postal Code' },
  { key: 'country', label: 'Country' },
];

function AddressFields({ value, editing, onChange, prefix }) {
  const addr = value || {};
  return (
    <Grid container spacing={2}>
      {ADDRESS_FIELDS.map(({ key, label }) => (
        <Grid item xs={12} sm={key === 'address' || key === 'street2' ? 12 : 6} key={key}>
          {editing ? (
            <TextField
              fullWidth size="small" label={label}
              name={`${prefix}.${key}`}
              value={addr[key] || ''}
              onChange={(e) => onChange(prefix, key, e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          ) : (
            <Box>
              <Typography variant="caption" color="text.secondary">{label}</Typography>
              <Typography variant="body2">{addr[key] || <em style={{ opacity: 0.5 }}>—</em>}</Typography>
            </Box>
          )}
        </Grid>
      ))}
    </Grid>
  );
}

export default function CompanyDetail({
  company,
  onBack,
  onUpdate,
  initialTab = 'overview',
  initialEditMode = false,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { showConfirm, ConfirmDialogComponent } = useConfirmDialog();
  const { isAdmin, isProgramAdmin } = useAuth();
  const canManageRecurring = isAdmin || isProgramAdmin;

  const [activeTab, setActiveTab] = useState(initialTab);
  const [editingOverview, setEditingOverview] = useState(initialEditMode);
  const [editingBilling, setEditingBilling] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state — separate copy that the user edits in place. On Save we POST
  // to the API and call onUpdate so the parent reloads the list. On Cancel we
  // reset to the latest `company` prop.
  const buildFormState = (c) => ({
    name: c?.name || '',
    description: c?.description || '',
    default_export_format: c?.default_export_format || 'standard',
    default_bill_rate: c?.default_bill_rate != null ? String(c.default_bill_rate) : '',
    holiday_bill_multiplier: c?.holiday_bill_multiplier != null ? String(c.holiday_bill_multiplier) : '1.00',
    payment_terms: c?.payment_terms || '',
    billing_address: c?.billing_address || {},
    shipping_address: c?.shipping_address || {},
    ferc_settings: c?.ferc_settings || {},
    invoice_numbering: c?.invoice_numbering || null, // null = tenant scheme
  });
  const [formData, setFormData] = useState(() => buildFormState(company));

  // Reset the form whenever the company prop changes (e.g. parent reloaded).
  useEffect(() => {
    setFormData(buildFormState(company));
  }, [company]);

  // Contacts
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [editingContactId, setEditingContactId] = useState(null);
  const [newContact, setNewContact] = useState(null);
  const [contactDraft, setContactDraft] = useState({
    name: '', email: '', phone: '', role: '', is_primary: false,
  });

  // Affiliates — sub-orgs scoped to this client (per-client affiliates).
  // The list endpoint returns this client's affiliates only when filtered
  // by company_id; we don't pull tenant-wide affiliates into this tab on
  // purpose so the user can see what's bound to this client at a glance.
  const [affiliates, setAffiliates] = useState([]);
  const [affiliatesLoading, setAffiliatesLoading] = useState(false);
  const [editingAffiliateId, setEditingAffiliateId] = useState(null);
  const [newAffiliate, setNewAffiliate] = useState(null);
  const [affiliateDraft, setAffiliateDraft] = useState({ name: '', description: '' });

  const loadContacts = useCallback(async () => {
    setContactsLoading(true);
    try {
      const res = await getCompanyContacts(company.id);
      setContacts(res.data);
    } catch (err) {
      setError('Failed to load contacts');
    } finally {
      setContactsLoading(false);
    }
  }, [company.id]);

  // Pull affiliates for this client only (company_id = X). Tenant-wide
  // affiliates are intentionally excluded from this tab — they're managed
  // on the top-level Affiliates page.
  const loadAffiliates = useCallback(async () => {
    setAffiliatesLoading(true);
    try {
      const res = await getAffiliates({ companyId: company.id });
      setAffiliates(res.data.filter(a => a.company_id === company.id));
    } catch (err) {
      setError('Failed to load affiliates');
    } finally {
      setAffiliatesLoading(false);
    }
  }, [company.id]);

  // Recurring contract entry schedules (admin only) — standing fixed-hours
  // entries for this client, auto-invoiced each semi-monthly period.
  const [recurringSchedules, setRecurringSchedules] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [recurringDraft, setRecurringDraft] = useState(null);
  const [recurringBusy, setRecurringBusy] = useState(false);

  const loadRecurring = useCallback(async () => {
    try {
      const [schedRes, empRes] = await Promise.all([getRecurringEntrySchedules(), getEmployees()]);
      setRecurringSchedules(schedRes.data.filter(s => s.company_id === company.id));
      setEmployees(empRes.data);
    } catch (err) {
      // Non-admins get 403 — section simply stays hidden
    }
  }, [company.id]);

  useEffect(() => {
    if (activeTab === 'contacts') loadContacts();
    if (activeTab === 'affiliates') loadAffiliates();
    if (activeTab === 'billing' && canManageRecurring) loadRecurring();
  }, [activeTab, loadContacts, loadAffiliates, loadRecurring, canManageRecurring]);

  const saveRecurringDraft = async () => {
    setRecurringBusy(true);
    setError('');
    try {
      if (recurringDraft.id) {
        await updateRecurringEntrySchedule(recurringDraft.id, recurringDraft);
      } else {
        await createRecurringEntrySchedule({ ...recurringDraft, company_id: company.id });
      }
      setRecurringDraft(null);
      setSuccess('Recurring entry schedule saved');
      await loadRecurring();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save schedule');
    } finally {
      setRecurringBusy(false);
    }
  };

  const removeRecurringSchedule = async (schedule) => {
    const ok = await showConfirm({
      title: 'Delete recurring schedule?',
      message: 'Already-generated time entries are kept; no new entries will be created.',
    });
    if (!ok) return;
    try {
      await deleteRecurringEntrySchedule(schedule.id);
      setSuccess('Schedule deleted');
      await loadRecurring();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete schedule');
    }
  };

  const runRecurringNow = async (schedule) => {
    setRecurringBusy(true);
    setError('');
    try {
      const res = await runRecurringEntrySchedule(schedule.id);
      setSuccess(`Generated ${res.data.entries_created} entries`);
      await loadRecurring();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to run schedule');
    } finally {
      setRecurringBusy(false);
    }
  };

  // ── Field handlers ─────────────────────────────────────────────────

  const handleField = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddressField = (prefix, key, value) => {
    setFormData(prev => ({
      ...prev,
      [prefix]: { ...(prev[prefix] || {}), [key]: value },
    }));
  };

  // FERC settings live in one JSONB blob; top-level keys (contract_id, notes)
  // and nested groups (send_to / agency / remit) edit in place.
  const handleFercField = (key, value) => {
    setFormData(prev => ({
      ...prev,
      ferc_settings: { ...(prev.ferc_settings || {}), [key]: value },
    }));
  };
  const handleFercNested = (group, key, value) => {
    setFormData(prev => ({
      ...prev,
      ferc_settings: {
        ...(prev.ferc_settings || {}),
        [group]: { ...((prev.ferc_settings || {})[group] || {}), [key]: value },
      },
    }));
  };

  // ── Save ────────────────────────────────────────────────────────────

  // Build a payload with only the fields owned by the active tab, so editing
  // Overview doesn't accidentally clobber Billing fields the user didn't touch
  // (and vice versa).
  const buildPayload = (tab) => {
    if (tab === 'overview') {
      return {
        name: formData.name,
        description: formData.description,
        billing_address: formData.billing_address,
        shipping_address: formData.shipping_address,
      };
    }
    if (tab === 'billing') {
      return {
        name: formData.name, // PUT requires name
        description: formData.description,
        default_export_format: formData.default_export_format,
        default_bill_rate: formData.default_bill_rate,
        holiday_bill_multiplier: formData.holiday_bill_multiplier,
        payment_terms: formData.payment_terms,
        ferc_settings: formData.ferc_settings,
        invoice_numbering: formData.invoice_numbering,
      };
    }
    return {};
  };

  const saveTab = async (tab) => {
    setSaving(true);
    setError('');
    try {
      const payload = buildPayload(tab);
      const res = await updateCompany(company.id, payload);
      setSuccess(`${tab === 'overview' ? 'Client info' : 'Billing & terms'} saved`);
      if (tab === 'overview') setEditingOverview(false);
      if (tab === 'billing') setEditingBilling(false);
      onUpdate?.(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const cancelTab = (tab) => {
    setFormData(buildFormState(company));
    if (tab === 'overview') setEditingOverview(false);
    if (tab === 'billing') setEditingBilling(false);
    setError('');
  };

  // ── Contact CRUD ───────────────────────────────────────────────────

  const startEditContact = (contact) => {
    setEditingContactId(contact.id);
    setContactDraft({
      name: contact.name, email: contact.email,
      phone: contact.phone || '', role: contact.role || '',
      is_primary: !!contact.is_primary,
    });
  };

  const startNewContact = () => {
    setNewContact(true);
    setEditingContactId(null);
    setContactDraft({ name: '', email: '', phone: '', role: '', is_primary: false });
  };

  const cancelContactEdit = () => {
    setEditingContactId(null);
    setNewContact(null);
  };

  const saveContact = async () => {
    setSaving(true);
    setError('');
    try {
      if (editingContactId) {
        await updateContact(editingContactId, contactDraft);
        setSuccess('Contact updated');
      } else {
        await createCompanyContact(company.id, contactDraft);
        setSuccess('Contact added');
      }
      setEditingContactId(null);
      setNewContact(null);
      loadContacts();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save contact');
    } finally {
      setSaving(false);
    }
  };

  const removeContact = async (contactId) => {
    const ok = await showConfirm({
      title: 'Delete Contact', message: 'Delete this contact?',
      confirmText: 'Delete', confirmColor: 'error', severity: 'warning',
    });
    if (!ok) return;
    try {
      await deleteContact(contactId);
      setSuccess('Contact deleted');
      loadContacts();
    } catch (err) {
      setError('Failed to delete contact');
    }
  };

  // ── Affiliate CRUD ─────────────────────────────────────────────────

  const startEditAffiliate = (a) => {
    setEditingAffiliateId(a.id);
    setAffiliateDraft({ name: a.name, description: a.description || '' });
  };

  const startNewAffiliate = () => {
    setNewAffiliate(true);
    setEditingAffiliateId(null);
    setAffiliateDraft({ name: '', description: '' });
  };

  const cancelAffiliateEdit = () => {
    setEditingAffiliateId(null);
    setNewAffiliate(null);
  };

  const saveAffiliate = async () => {
    setSaving(true);
    setError('');
    try {
      if (editingAffiliateId) {
        // Updating an existing per-client affiliate. company_id stays the
        // same; we don't expose moving an affiliate between clients here.
        await updateAffiliate(editingAffiliateId, { ...affiliateDraft, company_id: company.id });
        setSuccess('Affiliate updated');
      } else {
        await createAffiliate({ ...affiliateDraft, company_id: company.id });
        setSuccess('Affiliate added');
      }
      setEditingAffiliateId(null);
      setNewAffiliate(null);
      loadAffiliates();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save affiliate');
    } finally {
      setSaving(false);
    }
  };

  const removeAffiliate = async (id) => {
    const ok = await showConfirm({
      title: 'Delete Affiliate',
      message: 'Delete this affiliate? Time entries that reference it keep the historical link.',
      confirmText: 'Delete', confirmColor: 'error', severity: 'warning',
    });
    if (!ok) return;
    try {
      await deleteAffiliate(id);
      setSuccess('Affiliate deleted');
      loadAffiliates();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete affiliate');
    }
  };

  // ── Render ─────────────────────────────────────────────────────────

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={onBack} size="small">
          All Clients
        </Button>
        <Typography variant="h5" sx={{ flexGrow: 1, ml: 2 }}>
          {company.name}
        </Typography>
        {company.zoho_contact_id && (
          <Tooltip title={`Linked to Zoho contact ${company.zoho_contact_id}`}>
            <Chip
              icon={<ZohoIcon fontSize="small" />}
              label="Zoho"
              size="small"
              variant="outlined"
              color="primary"
            />
          </Tooltip>
        )}
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Paper>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant={isMobile ? 'fullWidth' : 'standard'}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab value="overview" label="Overview" icon={<Business fontSize="small" />} iconPosition="start" />
          <Tab value="billing" label="Billing & Terms" icon={<AttachMoney fontSize="small" />} iconPosition="start" />
          <Tab value="contacts" label="Contacts" icon={<ContactMail fontSize="small" />} iconPosition="start" />
          <Tab value="affiliates" label="Affiliates" icon={<AffiliateIcon fontSize="small" />} iconPosition="start" />
        </Tabs>

        {/* ───────────────── Overview tab ───────────────── */}
        {activeTab === 'overview' && (
          <Box sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ flexGrow: 1 }}>Client Info</Typography>
              {editingOverview ? (
                <Stack direction="row" spacing={1}>
                  <Button size="small" onClick={() => cancelTab('overview')} startIcon={<Cancel />}>Cancel</Button>
                  <Button
                    size="small" variant="contained"
                    onClick={() => saveTab('overview')}
                    disabled={saving || !formData.name}
                    startIcon={saving ? <CircularProgress size={16} /> : <Save />}>
                    Save
                  </Button>
                </Stack>
              ) : (
                <Button size="small" onClick={() => setEditingOverview(true)} startIcon={<Edit />}>
                  Edit
                </Button>
              )}
            </Stack>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                {editingOverview ? (
                  <TextField
                    fullWidth required size="small" label="Client Name"
                    name="name" value={formData.name} onChange={handleField}
                    InputLabelProps={{ shrink: true }}
                  />
                ) : (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Client Name</Typography>
                    <Typography variant="body1">{company.name}</Typography>
                  </Box>
                )}
              </Grid>
              <Grid item xs={12}>
                {editingOverview ? (
                  <TextField
                    fullWidth multiline rows={2} size="small" label="Description"
                    name="description" value={formData.description} onChange={handleField}
                    InputLabelProps={{ shrink: true }}
                  />
                ) : (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Description</Typography>
                    <Typography variant="body2">{company.description || <em style={{ opacity: 0.5 }}>—</em>}</Typography>
                  </Box>
                )}
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle1" sx={{ mb: 2 }}>Billing Address</Typography>
            <AddressFields
              value={editingOverview ? formData.billing_address : company.billing_address}
              editing={editingOverview}
              onChange={handleAddressField}
              prefix="billing_address"
            />

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle1" sx={{ mb: 2 }}>Shipping Address</Typography>
            <AddressFields
              value={editingOverview ? formData.shipping_address : company.shipping_address}
              editing={editingOverview}
              onChange={handleAddressField}
              prefix="shipping_address"
            />
          </Box>
        )}

        {/* ───────────────── Billing & Terms tab ───────────────── */}
        {activeTab === 'billing' && (
          <Box sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ flexGrow: 1 }}>Billing & Terms</Typography>
              {editingBilling ? (
                <Stack direction="row" spacing={1}>
                  <Button size="small" onClick={() => cancelTab('billing')} startIcon={<Cancel />}>Cancel</Button>
                  <Button
                    size="small" variant="contained"
                    onClick={() => saveTab('billing')}
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={16} /> : <Save />}>
                    Save
                  </Button>
                </Stack>
              ) : (
                <Button size="small" onClick={() => setEditingBilling(true)} startIcon={<Edit />}>
                  Edit
                </Button>
              )}
            </Stack>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                {editingBilling ? (
                  <TextField
                    fullWidth size="small" type="number" label="Default Bill Rate"
                    name="default_bill_rate" value={formData.default_bill_rate} onChange={handleField}
                    InputProps={{ startAdornment: '$' }}
                    inputProps={{ step: '0.01', min: 0 }}
                    InputLabelProps={{ shrink: true }}
                    helperText="Fallback hourly rate when no employee-specific rate is set."
                  />
                ) : (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Default Bill Rate</Typography>
                    <Typography variant="body1">
                      {company.default_bill_rate != null
                        ? `$${Number(company.default_bill_rate).toFixed(2)}/hr`
                        : <em style={{ opacity: 0.5 }}>—</em>}
                    </Typography>
                  </Box>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                {editingBilling ? (
                  <Tooltip title="Billing premium for hours worked on a holiday (per the employee's holiday schedule). 1.0 = no premium; 1.5 bills time-and-a-half. A per-assignment override can be set on the Bill Rates matrix." placement="top" arrow>
                    <TextField
                      fullWidth size="small" type="number" label="Holiday Bill Multiplier"
                      name="holiday_bill_multiplier" value={formData.holiday_bill_multiplier} onChange={handleField}
                      InputProps={{ startAdornment: '×' }}
                      inputProps={{ step: '0.05', min: 1, max: 3 }}
                      InputLabelProps={{ shrink: true }}
                      helperText="Premium billed for holiday work (1.0 = none)."
                    />
                  </Tooltip>
                ) : (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Holiday Bill Multiplier</Typography>
                    <Typography variant="body1">
                      {company.holiday_bill_multiplier != null && Number(company.holiday_bill_multiplier) > 1
                        ? `×${Number(company.holiday_bill_multiplier).toFixed(2)}`
                        : <em style={{ opacity: 0.5 }}>none</em>}
                    </Typography>
                  </Box>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                {editingBilling ? (
                  <TextField
                    fullWidth select size="small" label="Default Payment Terms"
                    name="payment_terms" value={formData.payment_terms} onChange={handleField}
                    InputLabelProps={{ shrink: true }}
                    helperText="Auto-fills the Terms field on new invoices for this customer. Leave blank to fall back to the tenant default."
                    SelectProps={{ displayEmpty: true }}
                  >
                    <MenuItem value=""><em>(Use tenant default)</em></MenuItem>
                    {TERMS_OPTIONS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </TextField>
                ) : (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Default Payment Terms</Typography>
                    <Typography variant="body1">{company.payment_terms || <em style={{ opacity: 0.5 }}>(uses tenant default)</em>}</Typography>
                  </Box>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                {editingBilling ? (
                  <TextField
                    fullWidth select size="small" label="Default Invoice Export Format"
                    name="default_export_format" value={formData.default_export_format} onChange={handleField}
                    InputLabelProps={{ shrink: true }}
                  >
                    {EXPORT_FORMATS.map(f => <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>)}
                  </TextField>
                ) : (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Default Invoice Export Format</Typography>
                    <Typography variant="body1">
                      {EXPORT_FORMATS.find(f => f.value === (company.default_export_format || 'standard'))?.label}
                    </Typography>
                  </Box>
                )}
              </Grid>

              {/* ── Invoice numbering — tenant scheme or per-client sequence ── */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                  <Typography variant="subtitle2">Invoice Numbering</Typography>
                  <Tooltip
                    title="How invoices for this client are numbered. Tenant default uses the shared INV-YYYY-##### sequence. A custom sequence numbers this client's invoices independently — set the prefix (optional), the next number to use, and zero-padding. The sequence always continues from this client's highest existing number, so lowering 'next number' never creates duplicates. If your Zoho numbering mode is 'Zoho assigns', don't push custom-numbered invoices to Zoho — the push would renumber them."
                    placement="top" arrow>
                    <InfoOutlined fontSize="small" color="action" />
                  </Tooltip>
                </Stack>
                {editingBilling ? (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth select size="small" label="Numbering Scheme"
                        value={formData.invoice_numbering ? 'custom' : 'tenant'}
                        onChange={e => setFormData(prev => ({
                          ...prev,
                          invoice_numbering: e.target.value === 'custom'
                            ? (prev.invoice_numbering || { prefix: '', next: 1, padding: 0 })
                            : null,
                        }))}
                        InputLabelProps={{ shrink: true }}
                      >
                        <MenuItem value="tenant">Tenant default (INV-YYYY-#####)</MenuItem>
                        <MenuItem value="custom">Custom sequence for this client</MenuItem>
                      </TextField>
                    </Grid>
                    {formData.invoice_numbering && (
                      <>
                        <Grid item xs={12} sm={3}>
                          <TextField fullWidth size="small" label="Prefix"
                            value={formData.invoice_numbering.prefix || ''}
                            onChange={e => setFormData(prev => ({
                              ...prev,
                              invoice_numbering: { ...prev.invoice_numbering, prefix: e.target.value },
                            }))}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ maxLength: 20 }}
                            helperText='Optional, e.g. "DHT-". Blank = bare numbers.' />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField fullWidth size="small" type="number" label="Next Number"
                            value={formData.invoice_numbering.next ?? ''}
                            onChange={e => setFormData(prev => ({
                              ...prev,
                              invoice_numbering: { ...prev.invoice_numbering, next: e.target.value === '' ? '' : parseInt(e.target.value, 10) },
                            }))}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ min: 1, step: 1 }}
                            helperText="The next invoice gets at least this number." />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <TextField fullWidth size="small" type="number" label="Zero Padding"
                            value={formData.invoice_numbering.padding ?? 0}
                            onChange={e => setFormData(prev => ({
                              ...prev,
                              invoice_numbering: { ...prev.invoice_numbering, padding: e.target.value === '' ? 0 : parseInt(e.target.value, 10) },
                            }))}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ min: 0, max: 8, step: 1 }}
                            helperText={`Next (at least): ${(formData.invoice_numbering.prefix || '') + String(formData.invoice_numbering.next || 1).padStart(formData.invoice_numbering.padding || 0, '0')}`} />
                        </Grid>
                      </>
                    )}
                  </Grid>
                ) : (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Scheme</Typography>
                      <Typography variant="body2">
                        {company.invoice_numbering
                          ? `Custom sequence — continues at ${(company.invoice_numbering.prefix || '') + String(company.invoice_numbering.next || 1).padStart(company.invoice_numbering.padding || 0, '0')} or after this client's highest number`
                          : <em style={{ opacity: 0.5 }}>Tenant default (INV-YYYY-#####)</em>}
                      </Typography>
                    </Grid>
                  </Grid>
                )}
              </Grid>

              {/* ── FERC invoice settings — only for FERC-format clients ── */}
              {(editingBilling ? formData.default_export_format : company.default_export_format) === 'ferc' && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                    <Typography variant="subtitle2">FERC Invoice Settings</Typography>
                    <Tooltip
                      title="Fills the fixed blocks of the FERC contractor Invoice for Hours format. Contract ID and PO # print in the top-right box. SEND TO is the prime contractor's accounts-payable contact. The agency block defaults to FERC's Washington DC office. Remit To defaults to the invoiced employee's profile (address and email) — set values here only to override."
                      placement="top" arrow>
                      <InfoOutlined fontSize="small" color="action" />
                    </Tooltip>
                  </Stack>
                  {editingBilling ? (
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={3}>
                        <TextField fullWidth size="small" label="Contract ID"
                          value={formData.ferc_settings?.contract_id || ''}
                          onChange={e => handleFercField('contract_id', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          helperText="Prints in the Contract ID box." />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField fullWidth size="small" label="Purchase Order #"
                          value={formData.ferc_settings?.po_number || ''}
                          onChange={e => handleFercField('po_number', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          helperText="Prints in the Purchase Order box." />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField fullWidth size="small" label="Customer / Contract Label"
                          value={formData.ferc_settings?.customer_contract || ''}
                          onChange={e => handleFercField('customer_contract', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          helperText='Line-item label (e.g. "FERC_DHT-PSO-RSTUDIO-1"). Blank = "Employee - Tenant".' />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField fullWidth size="small" label="Special Notes"
                          value={formData.ferc_settings?.notes || ''}
                          onChange={e => handleFercField('notes', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          helperText="Special Notes and Instructions box." />
                      </Grid>

                      <Grid item xs={12} sm={3}>
                        <TextField fullWidth size="small" label="Send To — Email"
                          value={formData.ferc_settings?.send_to?.email || ''}
                          onChange={e => handleFercNested('send_to', 'email', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          helperText="Prime contractor AP email." />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField fullWidth size="small" label="Send To — Name"
                          value={formData.ferc_settings?.send_to?.name || ''}
                          onChange={e => handleFercNested('send_to', 'name', e.target.value)}
                          InputLabelProps={{ shrink: true }} />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField fullWidth size="small" label="Send To — Address"
                          value={formData.ferc_settings?.send_to?.address1 || ''}
                          onChange={e => handleFercNested('send_to', 'address1', e.target.value)}
                          InputLabelProps={{ shrink: true }} />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField fullWidth size="small" label="Send To — City, State ZIP"
                          value={formData.ferc_settings?.send_to?.address2 || ''}
                          onChange={e => handleFercNested('send_to', 'address2', e.target.value)}
                          InputLabelProps={{ shrink: true }} />
                      </Grid>

                      <Grid item xs={12} sm={3}>
                        <TextField fullWidth size="small" label="Remit Phone"
                          value={formData.ferc_settings?.remit?.phone || ''}
                          onChange={e => handleFercNested('remit', 'phone', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          helperText="Contractor phone for the Remit To block and footer (not stored on the employee profile)." />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <TextField fullWidth size="small" label="Enquiries Contact Line"
                          value={formData.ferc_settings?.enquiries_contact || ''}
                          onChange={e => handleFercField('enquiries_contact', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          helperText='Optional footer sentence, e.g. "Should you have any enquiries concerning this invoice, please contact …".' />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <Tooltip title="Names the downloaded file. Tokens: {company}, {number}, {invoice_date}, {period_start}, {period_end} — dates render as YYYY_MM_DD. Example: invoice_{period_end} → invoice_2026_08_31.xlsx. Blank uses {company}_{number}_{invoice_date}." placement="top" arrow>
                          <TextField fullWidth size="small" label="Export Filename Pattern"
                            value={formData.ferc_settings?.filename_pattern || ''}
                            onChange={e => handleFercField('filename_pattern', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            placeholder="{company}_{number}_{invoice_date}"
                            helperText='e.g. "invoice_{period_end}" → invoice_2026_08_31.xlsx' />
                        </Tooltip>
                      </Grid>
                    </Grid>
                  ) : (
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">Contract ID</Typography>
                        <Typography variant="body2">{company.ferc_settings?.contract_id || <em style={{ opacity: 0.5 }}>—</em>}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">Purchase Order #</Typography>
                        <Typography variant="body2">{company.ferc_settings?.po_number || <em style={{ opacity: 0.5 }}>—</em>}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">Send To</Typography>
                        <Typography variant="body2">
                          {[company.ferc_settings?.send_to?.name, company.ferc_settings?.send_to?.email]
                            .filter(Boolean).join(' — ') || <em style={{ opacity: 0.5 }}>—</em>}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">Customer / Contract Label</Typography>
                        <Typography variant="body2">{company.ferc_settings?.customer_contract || <em style={{ opacity: 0.5 }}>Employee - Tenant</em>}</Typography>
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              )}

              {/* ── Recurring contract entries — admin only ── */}
              {canManageRecurring && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                    <Typography variant="subtitle2">Recurring Contract Entries</Typography>
                    <Tooltip
                      title="Standing salaried-contract schedule: creates a fixed-hours time entry for the selected employee on every weekday, automatically skipping the employee's holiday-schedule holidays (federal holidays included). When Auto-invoice is on, the just-finished semi-monthly period (1st-15th / 16th-end) is bundled into a DRAFT invoice for this client on the 1st and 16th — using this client's numbering, terms, and export format. Days already holding an entry are never duplicated."
                      placement="top" arrow>
                      <InfoOutlined fontSize="small" color="action" />
                    </Tooltip>
                    {!recurringDraft && (
                      <Button size="small" startIcon={<Add />} sx={{ ml: 'auto' }}
                        onClick={() => setRecurringDraft({
                          employee_id: '', hours_per_day: 8, start_time: '08:00',
                          description: 'Contract services',
                          start_date: new Date().toISOString().split('T')[0],
                          end_date: '', auto_invoice: true, is_active: true,
                        })}>
                        Add Schedule
                      </Button>
                    )}
                  </Stack>

                  {recurringSchedules.length === 0 && !recurringDraft && (
                    <Typography variant="body2" color="text.secondary"><em>No recurring schedules for this client.</em></Typography>
                  )}

                  {recurringSchedules.map(s => (
                    recurringDraft?.id === s.id ? null : (
                      <Paper key={s.id} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                        <Stack direction={isMobile ? 'column' : 'row'} spacing={1} alignItems={isMobile ? 'stretch' : 'center'}>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="body2">
                              {s.first_name} {s.last_name} — {Number(s.hours_per_day)} hrs every weekday
                              {' '}from {new Date(s.start_date).toLocaleDateString('en-US', { timeZone: 'UTC' })}
                              {s.end_date ? ` to ${new Date(s.end_date).toLocaleDateString('en-US', { timeZone: 'UTC' })}` : ''}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {s.is_active ? 'Active' : 'Paused'} · Auto-invoice each period: {s.auto_invoice ? 'on' : 'off'}
                              {s.last_generated_date ? ` · entries through ${new Date(s.last_generated_date).toLocaleDateString('en-US', { timeZone: 'UTC' })}` : ''}
                            </Typography>
                          </Box>
                          <Stack direction="row" spacing={1}>
                            <Tooltip title="Create any missing entries up to today right now" arrow>
                              <Button size="small" disabled={recurringBusy} onClick={() => runRecurringNow(s)}>Run Now</Button>
                            </Tooltip>
                            <Button size="small" onClick={() => setRecurringDraft({ ...s, end_date: s.end_date ? String(s.end_date).split('T')[0] : '', start_date: String(s.start_date).split('T')[0] })}>Edit</Button>
                            <Button size="small" color="error" onClick={() => removeRecurringSchedule(s)}>Delete</Button>
                          </Stack>
                        </Stack>
                      </Paper>
                    )
                  ))}

                  {recurringDraft && (
                    <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={3}>
                          <TextField fullWidth select size="small" label="Employee"
                            value={recurringDraft.employee_id}
                            disabled={!!recurringDraft.id}
                            onChange={e => setRecurringDraft(prev => ({ ...prev, employee_id: e.target.value }))}
                            InputLabelProps={{ shrink: true }}
                            helperText="Whose entries (and pay) these are.">
                            {employees.filter(e => e.is_active !== false).map(e => (
                              <MenuItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={6} sm={2}>
                          <TextField fullWidth size="small" type="number" label="Hours / Day"
                            value={recurringDraft.hours_per_day}
                            onChange={e => setRecurringDraft(prev => ({ ...prev, hours_per_day: e.target.value }))}
                            inputProps={{ min: 0.25, max: 24, step: 0.25 }}
                            InputLabelProps={{ shrink: true }} />
                        </Grid>
                        <Grid item xs={6} sm={2}>
                          <TextField fullWidth size="small" type="date" label="Start Date"
                            value={recurringDraft.start_date}
                            onChange={e => setRecurringDraft(prev => ({ ...prev, start_date: e.target.value }))}
                            InputLabelProps={{ shrink: true }}
                            helperText="Backfills from here." />
                        </Grid>
                        <Grid item xs={6} sm={2}>
                          <TextField fullWidth size="small" type="date" label="End Date"
                            value={recurringDraft.end_date}
                            onChange={e => setRecurringDraft(prev => ({ ...prev, end_date: e.target.value }))}
                            InputLabelProps={{ shrink: true }}
                            helperText="Blank = open-ended." />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <TextField fullWidth size="small" label="Entry Description"
                            value={recurringDraft.description}
                            onChange={e => setRecurringDraft(prev => ({ ...prev, description: e.target.value }))}
                            InputLabelProps={{ shrink: true }}
                            helperText="Appears on invoice lines." />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <TextField select size="small" label="Auto-invoice each period" sx={{ minWidth: 200 }}
                              value={recurringDraft.auto_invoice ? 'yes' : 'no'}
                              onChange={e => setRecurringDraft(prev => ({ ...prev, auto_invoice: e.target.value === 'yes' }))}
                              InputLabelProps={{ shrink: true }}>
                              <MenuItem value="yes">Yes — draft invoice on the 1st &amp; 16th</MenuItem>
                              <MenuItem value="no">No — entries only</MenuItem>
                            </TextField>
                            <TextField select size="small" label="Status" sx={{ minWidth: 120 }}
                              value={recurringDraft.is_active ? 'active' : 'paused'}
                              onChange={e => setRecurringDraft(prev => ({ ...prev, is_active: e.target.value === 'active' }))}
                              InputLabelProps={{ shrink: true }}>
                              <MenuItem value="active">Active</MenuItem>
                              <MenuItem value="paused">Paused</MenuItem>
                            </TextField>
                          </Stack>
                        </Grid>
                        <Grid item xs={12}>
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button size="small" onClick={() => setRecurringDraft(null)} startIcon={<Cancel />}>Cancel</Button>
                            <Button size="small" variant="contained" disabled={recurringBusy || !recurringDraft.employee_id}
                              onClick={saveRecurringDraft}
                              startIcon={recurringBusy ? <CircularProgress size={16} /> : <Save />}>
                              Save Schedule
                            </Button>
                          </Stack>
                        </Grid>
                      </Grid>
                    </Paper>
                  )}
                </Grid>
              )}
            </Grid>
          </Box>
        )}

        {/* ───────────────── Contacts tab ───────────────── */}
        {activeTab === 'contacts' && (
          <Box sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ flexGrow: 1 }}>Contacts</Typography>
              <Button size="small" startIcon={<Add />} variant="contained" onClick={startNewContact} disabled={!!newContact || !!editingContactId}>
                Add Contact
              </Button>
            </Stack>

            {contactsLoading ? (
              <CircularProgress size={20} />
            ) : (
              <Stack spacing={1.5}>
                {newContact && (
                  <ContactEditRow
                    draft={contactDraft} setDraft={setContactDraft}
                    onSave={saveContact} onCancel={cancelContactEdit}
                    saving={saving} isMobile={isMobile}
                  />
                )}
                {contacts.length === 0 && !newContact && (
                  <Alert severity="info">No contacts yet. Click "Add Contact" to add one.</Alert>
                )}
                {contacts.map(c => (
                  editingContactId === c.id ? (
                    <ContactEditRow
                      key={c.id}
                      draft={contactDraft} setDraft={setContactDraft}
                      onSave={saveContact} onCancel={cancelContactEdit}
                      saving={saving} isMobile={isMobile}
                    />
                  ) : (
                    <Paper key={c.id} variant="outlined" sx={{ p: 2 }}>
                      <Stack direction={isMobile ? 'column' : 'row'} spacing={1} alignItems={isMobile ? 'stretch' : 'center'}>
                        <Box sx={{ flexGrow: 1 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            {c.is_primary ? <Star fontSize="small" color="warning" /> : <Person fontSize="small" color="action" />}
                            <Typography variant="subtitle2">{c.name}</Typography>
                            {c.role && <Chip size="small" label={c.role} variant="outlined" />}
                            {c.zoho_contact_person_id && (
                              <Tooltip title={`Linked to Zoho contact person ${c.zoho_contact_person_id}`}>
                                <Chip
                                  icon={<ZohoIcon fontSize="small" />}
                                  label="from Zoho"
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                />
                              </Tooltip>
                            )}
                          </Stack>
                          <Stack direction="row" spacing={2} sx={{ mt: 0.5, color: 'text.secondary' }}>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Email fontSize="inherit" />
                              <Typography variant="body2">{c.email}</Typography>
                            </Stack>
                            {c.phone && (
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                <Phone fontSize="inherit" />
                                <Typography variant="body2">{c.phone}</Typography>
                              </Stack>
                            )}
                          </Stack>
                        </Box>
                        <Stack direction="row" spacing={0.5}>
                          <IconButton size="small" onClick={() => startEditContact(c)} aria-label={`Edit ${c.name}`}>
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => removeContact(c.id)} aria-label={`Delete ${c.name}`}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Stack>
                    </Paper>
                  )
                ))}
              </Stack>
            )}
          </Box>
        )}

        {/* ───────────────── Affiliates tab ───────────────── */}
        {activeTab === 'affiliates' && (
          <Box sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ flexGrow: 1 }}>Affiliates</Typography>
              <Button
                size="small" startIcon={<Add />} variant="contained"
                onClick={startNewAffiliate}
                disabled={!!newAffiliate || !!editingAffiliateId}>
                Add Affiliate
              </Button>
            </Stack>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Sub-organizations of {company.name} — selectable on time entries when this client is picked.
              Tenant-wide affiliates that aren't tied to any client are managed on the top-level Affiliates page.
            </Typography>

            {affiliatesLoading ? (
              <CircularProgress size={20} />
            ) : (
              <Stack spacing={1.5}>
                {newAffiliate && (
                  <AffiliateEditRow
                    draft={affiliateDraft} setDraft={setAffiliateDraft}
                    onSave={saveAffiliate} onCancel={cancelAffiliateEdit}
                    saving={saving} isMobile={isMobile}
                  />
                )}
                {affiliates.length === 0 && !newAffiliate && (
                  <Alert severity="info">
                    No affiliates yet for this client. Click "Add Affiliate" to add one.
                  </Alert>
                )}
                {affiliates.map(a => (
                  editingAffiliateId === a.id ? (
                    <AffiliateEditRow
                      key={a.id}
                      draft={affiliateDraft} setDraft={setAffiliateDraft}
                      onSave={saveAffiliate} onCancel={cancelAffiliateEdit}
                      saving={saving} isMobile={isMobile}
                    />
                  ) : (
                    <Paper key={a.id} variant="outlined" sx={{ p: 2 }}>
                      <Stack direction={isMobile ? 'column' : 'row'} spacing={1} alignItems={isMobile ? 'stretch' : 'center'}>
                        <Box sx={{ flexGrow: 1 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <AffiliateIcon fontSize="small" color="action" />
                            <Typography variant="subtitle2">{a.name}</Typography>
                          </Stack>
                          {a.description && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              {a.description}
                            </Typography>
                          )}
                        </Box>
                        <Stack direction="row" spacing={0.5}>
                          <IconButton size="small" onClick={() => startEditAffiliate(a)} aria-label={`Edit ${a.name}`}>
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => removeAffiliate(a.id)} aria-label={`Delete ${a.name}`}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Stack>
                    </Paper>
                  )
                ))}
              </Stack>
            )}
          </Box>
        )}
      </Paper>

      <ConfirmDialogComponent />
    </Box>
  );
}

function ContactEditRow({ draft, setDraft, onSave, onCancel, saving, isMobile }) {
  const set = (field) => (e) => {
    const { value, type, checked } = e.target;
    setDraft(prev => ({ ...prev, [field]: type === 'checkbox' ? checked : value }));
  };
  return (
    <Paper variant="outlined" sx={{ p: 2, borderColor: 'primary.main' }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth required size="small" label="Name" value={draft.name} onChange={set('name')} InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth required size="small" type="email" label="Email" value={draft.email} onChange={set('email')} InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size="small" label="Phone" value={draft.phone} onChange={set('phone')} InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size="small" label="Role" value={draft.role} onChange={set('role')} InputLabelProps={{ shrink: true }} placeholder="e.g. Billing, Accounts Payable" />
        </Grid>
        <Grid item xs={12}>
          <Button
            size="small"
            startIcon={draft.is_primary ? <Star color="warning" /> : <StarBorder />}
            onClick={() => setDraft(prev => ({ ...prev, is_primary: !prev.is_primary }))}
          >
            {draft.is_primary ? 'Primary contact' : 'Mark as primary'}
          </Button>
        </Grid>
        <Grid item xs={12}>
          <Stack direction={isMobile ? 'column' : 'row'} spacing={1} justifyContent="flex-end">
            <Button onClick={onCancel} startIcon={<Cancel />}>Cancel</Button>
            <Button
              variant="contained"
              onClick={onSave}
              disabled={saving || !draft.name || !draft.email}
              startIcon={saving ? <CircularProgress size={16} /> : <Save />}>
              Save Contact
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  );
}

function AffiliateEditRow({ draft, setDraft, onSave, onCancel, saving, isMobile }) {
  const set = (field) => (e) => setDraft(prev => ({ ...prev, [field]: e.target.value }));
  return (
    <Paper variant="outlined" sx={{ p: 2, borderColor: 'primary.main' }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth required size="small" label="Affiliate Name"
            value={draft.name} onChange={set('name')}
            InputLabelProps={{ shrink: true }}
            placeholder="e.g. Tier 1 Support, Pharmacy Division"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth size="small" label="Description"
            value={draft.description} onChange={set('description')}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12}>
          <Stack direction={isMobile ? 'column' : 'row'} spacing={1} justifyContent="flex-end">
            <Button onClick={onCancel} startIcon={<Cancel />}>Cancel</Button>
            <Button
              variant="contained"
              onClick={onSave}
              disabled={saving || !draft.name}
              startIcon={saving ? <CircularProgress size={16} /> : <Save />}>
              Save Affiliate
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  );
}
